"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Shows a loader on every route change.
 * - minDuration avoids flicker on fast transitions.
 * - delay can hold the start a tick if you want (default 0).
 */
export default function useRouteLoader({
  minDuration = 500,
  delay = 0,
}: { minDuration?: number; delay?: number } = {}) {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const startRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Route changed -> start loader
    if (delay > 0) {
      timerRef.current = setTimeout(() => {
        setIsLoading(true);
        startRef.current = Date.now();
      }, delay);
    } else {
      setIsLoading(true);
      startRef.current = Date.now();
    }

    // When the new route has rendered, close after minDuration
    const done = () => {
      const elapsed = (startRef.current ? Date.now() - startRef.current : 0);
      const remaining = Math.max(minDuration - elapsed, 0);
      timerRef.current = setTimeout(() => {
        setIsLoading(false);
        startRef.current = null;
      }, remaining);
    };

    // next/navigation doesn’t expose "complete" events.
    // We approximate by finishing on next paint + minDuration.
    // This feels great in practice for most app-router cases.
    requestAnimationFrame(done);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return isLoading;
}